import { ISetNewPasswordFormValues } from '@common/types/user';
import { ButtonLink } from '@components/utility/buttons';
import { INVALID_PASSWORD_ERROR_REGEX } from '@constants/user';
import { useLocaleFormRevalidation } from '@hooks/useLocaleFormRevalidation';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    Alert,
    Button,
    Collapse,
    IconButton,
    InputAdornment,
    InputLabel,
    Link as MUILink,
    TextField,
    Typography,
} from '@mui/material';
import { createSetNewPasswordValidationSchema } from '@schemas/Authentication/SetNewPasswordSchema';
import { useSetNewUserPasswordMutation } from '@services/accounts';
import { handleError } from '@utils/common';
import { generateSecurePassword } from '@utils/user';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';

const SetNewPasswordForm = () => {
    const { formatMessage, locale } = useIntl();
    const SetNewPasswordSchema = useMemo(() => createSetNewPasswordValidationSchema(formatMessage), [formatMessage]);
    const [searchParams, _setSearchParams] = useSearchParams();
    const password_hash = searchParams.get('password_hash');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState<string>();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorAlert, setErrorAlert] = useState<
        'password-update-failed' | 'password-hash-invalid' | 'network-error' | null
    >(null);
    const navigate = usePrefixedNavigate();

    const [setNewUserPassword] = useSetNewUserPasswordMutation();

    useEffect(() => {
        if (!password_hash) navigate({ pathname: '/auth/login', search: searchParams.toString() }, { replace: true });
    }, [password_hash, navigate]);

    const formik = useFormik<ISetNewPasswordFormValues>({
        initialValues: {
            new_password: '',
            confirm_new_password: '',
        },
        validationSchema: SetNewPasswordSchema,
        onSubmit: async (_values, { setErrors, setTouched }) => {
            setIsSubmitting(true);
            setErrorAlert(null);
            try {
                handleSetNewPassword();
            } catch (error: unknown) {
                if (error && typeof error === 'object' && 'inner' in error) {
                    const validationErrors: Record<string, string> = {};
                    const touchedFields: Record<string, boolean> = {};

                    (error as { inner: Array<{ path?: string; message: string }> }).inner.forEach((err) => {
                        if (err.path) {
                            validationErrors[err.path] = err.message;
                            touchedFields[err.path] = true;
                        }
                    });

                    setErrors(validationErrors);
                    setTouched(touchedFields);
                }
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const { values, errors, touched, setValues, setFieldValue, handleSubmit, handleBlur } = formik;

    useLocaleFormRevalidation(formik, locale);

    const handleSetNewPassword = async () => {
        try {
            if (password_hash) {
                await setNewUserPassword({ password: values.new_password, password_hash }).unwrap();
                navigate(
                    { pathname: '/auth/new-password-success', search: window.location.search },
                    {
                        state: { 'new-password-allow': true },
                    }
                );
            }
        } catch (error: unknown) {
            if (typeof error === 'object' && error !== null && 'status' in error) {
                const statusError = error as { status?: number; data?: { error: string } };
                if (
                    statusError.status === 404 &&
                    INVALID_PASSWORD_ERROR_REGEX.test(statusError.data?.error.toLowerCase() ?? '')
                ) {
                    setErrorAlert('password-hash-invalid');
                } else {
                    setErrorAlert('password-update-failed');
                }
            } else {
                setErrorAlert('network-error');
            }
        }
    };

    const handleGeneratePassword = useCallback(() => {
        try {
            const newPassword = generateSecurePassword();
            setGeneratedPassword(newPassword);
            setValues({ new_password: newPassword, confirm_new_password: newPassword });
            setShowNewPassword(true);
            setShowConfirmPassword(true);
        } catch (err: unknown) {
            handleError(
                formatMessage({
                    id: 'auth.setNewPassword.generatePasswordError',
                    defaultMessage: 'An error occurred while generating a password',
                }),
                err
            );
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, []);

    const isSubmitDisabled = useMemo(
        () => Object.keys(errors).length > 0 || !values.new_password || !values.confirm_new_password,
        [errors, values.new_password, values.confirm_new_password]
    );

    const handleToggleNewPassword = () => setShowNewPassword(!showNewPassword);
    const handleToggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (generatedPassword) {
            setValues({
                new_password: event.target.value,
                confirm_new_password: '',
            });
        } else {
            setFieldValue('new_password', event.target.value);
        }
    };

    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue('confirm_new_password', event.target.value);
    };

    const handleErrorAlertClose = () => {
        setErrorAlert(null);
    };

    const ErrorAlertContent = () => {
        if (errorAlert === 'password-update-failed') {
            return (
                <Typography fontFamily="Roboto" fontSize={16}>
                    <FormattedMessage
                        id="auth.setNewPassword.errors.updateFailed"
                        defaultMessage="Failed to update password. Please try again later."
                        description="Error message when password update fails. Used in: /auth/set-new-password"
                    />
                </Typography>
            );
        } else if (errorAlert === 'password-hash-invalid') {
            return (
                <Typography fontFamily="Roboto" fontSize={16}>
                    <FormattedMessage
                        id="auth.setNewPassword.errors.linkExpired"
                        defaultMessage="This link has expired or is invalid, please restart the forgot password process {hereLink}."
                        description="Error message when reset password link is expired or invalid. Used in: /auth/set-new-password"
                        values={{
                            hereLink: (
                                <MUILink
                                    to="../forgot-password"
                                    component={Link}
                                    underline="none"
                                    className="m-0"
                                    tabIndex={-1}
                                    fontFamily="Roboto"
                                    fontSize={16}
                                >
                                    <FormattedMessage
                                        id="auth.setNewPassword.errors.here"
                                        defaultMessage="here"
                                        description="Link text to restart forgot password process. Used in: /auth/set-new-password"
                                    />
                                </MUILink>
                            ),
                        }}
                    />
                </Typography>
            );
        } else if (errorAlert === 'network-error') {
            return (
                <Typography fontFamily="Roboto" fontSize={16}>
                    <FormattedMessage
                        id="auth.setNewPassword.errors.networkError"
                        defaultMessage="Network error occurred. Please check your connection and try again."
                        description="Error message for network connectivity issues. Used in: /auth/set-new-password"
                    />
                </Typography>
            );
        }
        return null;
    };

    useEffect(() => {
        if (
            generatedPassword &&
            (values.new_password !== generatedPassword || values.confirm_new_password !== generatedPassword)
        ) {
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [values.new_password, values.confirm_new_password, generatedPassword]);

    return (
        <div className="w-full mt-4">
            <form onSubmit={handleSubmit} noValidate>
                {errorAlert ? (
                    <Collapse in={!!errorAlert}>
                        <Alert severity="error" className="mb-6" onClose={handleErrorAlertClose}>
                            <ErrorAlertContent />
                        </Alert>
                    </Collapse>
                ) : null}
                <div className="flex flex-col justify-end items-end mb-6">
                    <div className="w-full">
                        <InputLabel htmlFor="new-password" className="block text-sm font-medium text-neutral-950 mb-2">
                            <FormattedMessage
                                id="auth.fields.password.new"
                                defaultMessage="New password"
                                description="Label for new password input field. Used in: /auth/set-new-password"
                            />
                        </InputLabel>
                        <TextField
                            type={showNewPassword ? 'text' : 'password'}
                            id="new-password"
                            name="new_password"
                            value={values.new_password}
                            onChange={handleNewPasswordChange}
                            onBlur={handleBlur}
                            fullWidth
                            variant="outlined"
                            placeholder={formatMessage({
                                id: 'auth.fields.password.newPlaceholder',
                                defaultMessage: 'New password',
                            })}
                            disabled={isSubmitting}
                            error={Boolean(touched.new_password && errors.new_password)}
                            helperText={touched.new_password && errors.new_password}
                            size="small"
                            margin="none"
                            slotProps={{
                                input: {
                                    className: ' rounded-lg',
                                    endAdornment: (
                                        <InputAdornment position="end" tabIndex={-1}>
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleToggleNewPassword}
                                                edge="end"
                                                size="small"
                                                tabIndex={-1}
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </div>
                    <ButtonLink
                        align="right"
                        className="block mt-2 w-fit"
                        fontFamily="Open Sans"
                        fontSize={14}
                        onClick={() => handleGeneratePassword()}
                        tabIndex={-1}
                    >
                        <FormattedMessage
                            id="auth.setNewPassword.generatePassword"
                            defaultMessage="Generate Password"
                            description="Text for generate password button. Used in: /auth/set-new-password"
                        />
                    </ButtonLink>
                </div>
                <div className="mb-6">
                    <InputLabel
                        htmlFor="confirm-new-password"
                        className="block text-sm font-medium text-neutral-950 mb-2"
                    >
                        <FormattedMessage
                            id="auth.fields.newPassword.confirm"
                            defaultMessage="Confirm new password"
                            description="Label for password confirmation field. Used in: app/auth/new-password"
                        />
                    </InputLabel>
                    <TextField
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirm-new-password"
                        name="confirm_new_password"
                        value={values.confirm_new_password}
                        onChange={handleConfirmPasswordChange}
                        onBlur={handleBlur}
                        fullWidth
                        variant="outlined"
                        placeholder={formatMessage({
                            id: 'auth.fields.newPassword.confirmPlaceholder',
                            defaultMessage: 'Confirm new password',
                            description: 'Label for new password confirmation field. Used in: app/auth/new-password',
                        })}
                        disabled={isSubmitting}
                        error={Boolean(touched.confirm_new_password && errors.confirm_new_password)}
                        helperText={touched.confirm_new_password && errors.confirm_new_password}
                        size="small"
                        margin="none"
                        slotProps={{
                            input: {
                                className: ' rounded-lg',
                                endAdornment: (
                                    <InputAdornment position="end" tabIndex={-1}>
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleToggleConfirmPassword}
                                            edge="end"
                                            size="small"
                                            tabIndex={-1}
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </div>
                <div className="flex flex-col gap-4">
                    <Button type="submit" disabled={isSubmitDisabled} fullWidth className="rounded-full black-border">
                        <FormattedMessage
                            id="auth.setNewPassword.submit"
                            defaultMessage="Set new password"
                            description="Text for set new password button. Used in: /auth/set-new-password"
                        />
                    </Button>
                    <Button
                        type="button"
                        fullWidth
                        className="rounded-full black-border mb-4"
                        color="white"
                        component={Link}
                        to={{
                            pathname: '../login',
                            search: searchParams.toString(),
                        }}
                    >
                        <FormattedMessage
                            id="auth.setNewPassword.returnToLogin"
                            defaultMessage="Return to login"
                            description="Text for return to login button. Used in: /auth/set-new-password"
                        />
                    </Button>
                </div>
            </form>
        </div>
    );
};

export { SetNewPasswordForm };
